import React, { FunctionComponent, useEffect, useMemo, useState } from "react"
import { Route, useHistory, useRouteMatch } from "react-router"

import Seo from "../../../components/Seo/Seo"
import Loader from "../../../components/Loader/Loader"
import { iArtPiece } from "../../../definitions/ArtPieces"
import {
  handleDeleteData,
  get,
  handleUpdateData,
  post,
} from "../../../store/data/actions"
import { iResponse } from "../../../definitions/interfaces"
import { FormProvider, useForm } from "react-hook-form"
import ArtPieceOverview from "./Overview"
import ArtPieceUploads from "./Uploads"
import ArtPieceSaleInfo from "./SaleInfo"
import { store } from "react-notifications-component"
import { notificationOptions } from "../../../api/Definitions"
import { iAccess } from "../../../definitions/Access"
import ArtPieceAccess from "./Access"
import { unstable_batchedUpdates } from "react-dom"
import ArtPiecePainting from "./Painting"
import ArtPieceDrawing from "./Drawing"
import ArtPieceSculpture from "./Sculpture"
import ArtPiecePrint from "./Print"
import ArtPiecePhotography from "./Photography"
import ArtPieceVideo from "./Video"
import ArtPieceStreetArt from "./StreetArt"
import ArtPieceAudio from "./Audio"
import { ItemWithAccess } from "../../../definitions/types"
import ArtPieceHeader from "../Header"

interface iArtPieceParams {
  url: string
}

interface iProps {
  item: iArtPiece
  access: iAccess
  reloadArtPiece: () => void
}

const Inspector: FunctionComponent<iProps> = ({
  item,
  access,
  reloadArtPiece,
}) => {
  const [isLoading, setLoading] = useState(false)
  const match = useRouteMatch<iArtPieceParams>()
  const history = useHistory()

  const methods = useForm<ItemWithAccess<iArtPiece>>({
    defaultValues: { ...item, __access: access },
    shouldUnregister: false,
  })

  console.log(item)

  const {
    formState: { dirtyFields },
  } = methods

  useEffect(() => {
    methods.reset({ ...item, __access: access })
  }, [item, access])

  const onSubmit = (data: ItemWithAccess<iArtPiece>) => {
    setLoading(true)
    handleUpdateData<ItemWithAccess<iArtPiece>>({
      data,
      fields: dirtyFields as Partial<ItemWithAccess<iArtPiece>>,
      link: "/v1/art-piece",
    })
      .then(() => {
        setLoading(false)
        reloadArtPiece()
        store.addNotification({
          title: "Success!",
          message: "Art piece successfully updated!",
          // message: "teodosii@react-notifications-component",
          type: "success",
          ...notificationOptions,
        })
      })
      .catch((e) => {
        if (e) {
          if (e.reason === "Nothing changed!") {
            store.addNotification({
              message: e.reason,
              type: "warning",
              ...notificationOptions,
            })
          } else {
            store.addNotification({
              message: e.reason,
              type: "danger",
              ...notificationOptions,
            })
          }
        } else {
          store.addNotification({
            message: "Something went wrong!",
            type: "danger",
            ...notificationOptions,
          })
        }
        setLoading(false)
      })
  }

  const onDelete = () => {
    setLoading(true)
    if (confirm("Are you sure you want to delete this?")) {
      handleDeleteData({
        link: "/v1/art-pieces/" + item._id,
      })
        .then(() => {
          setLoading(false)
          store.addNotification({
            title: "Success!",
            message: "Art piece successfully deleted!",
            // message: "teodosii@react-notifications-component",
            type: "success",
            ...notificationOptions,
          })
          history.goBack()
        })
        .catch((e) => {
          if (e) {
            if (e.reason === "Nothing changed!") {
              store.addNotification({
                message: e.reason,
                type: "warning",
                ...notificationOptions,
              })
            } else {
              store.addNotification({
                message: e.reason,
                type: "danger",
                ...notificationOptions,
              })
            }
          } else {
            store.addNotification({
              message: "Something went wrong!",
              type: "danger",
              ...notificationOptions,
            })
          }
          setLoading(false)
        })
    }
  }

  const createAudio = (data: any) => {
    setLoading(true)

    post<any>({
      link: "/v2/art-piece-text-to-speech",
      data: { _id: item._id, data },
    }).then((s) => {
      setLoading(false)
      reloadArtPiece()
    })
  }

  const invokeSubmit = () => {
    methods.handleSubmit((data) => {
      onSubmit(data as ItemWithAccess<iArtPiece>)
    })()
  }

  return (
    <FormProvider {...methods}>
      <Seo
        title={`${item?.shrTitle ? item.shrTitle : "Inspector"}  | VR-All-Art`}
      />
      {isLoading ? (
        <Loader />
      ) : (
        <>
          <ArtPieceHeader
            title={item.shrTitle}
            access={access}
            artPieceType={item.shrType}
            published={!!item.published}
            submitDeleteText={"Delete"}
            submitText={"Update"}
            handleSubmit={invokeSubmit}
            handleDelete={onDelete}
            cover={item.shrPreviewImageId}
          />
          <div className="inspector">
            <form
              onSubmit={methods.handleSubmit((data) => {
                onSubmit(data as ItemWithAccess<iArtPiece>)
              })}
              className="inspector-form"
            >
              <Route exact path={match.path}>
                <ArtPieceOverview onCreateAudio={createAudio} />
              </Route>
              <Route exact path={match.path + "/manage-files"}>
                <ArtPieceUploads item={item} />
              </Route>
              <Route exact path={match.path + "/sale-info"}>
                <ArtPieceSaleInfo />
              </Route>
              {access && (
                <Route exact path={match.path + "/access"}>
                  <ArtPieceAccess access={access} />
                </Route>
              )}
              {item?.shrType === "PAINTING" && (
                <Route exact path={match.path + "/painting"}>
                  <ArtPiecePainting />
                </Route>
              )}
              {item?.shrType === "DRAWING" && (
                <Route exact path={match.path + "/drawing"}>
                  <ArtPieceDrawing />
                </Route>
              )}
              {item.shrType === "SCULPTURE" && (
                <Route exact path={match.path + "/sculpture"}>
                  <ArtPieceSculpture />
                </Route>
              )}
              {item.shrType === "PRINT" && (
                <Route exact path={match.path + "/print"}>
                  <ArtPiecePrint />
                </Route>
              )}
              {item.shrType === "PHOTOGRAPHY" && (
                <Route exact path={match.path + "/photography"}>
                  <ArtPiecePhotography />
                </Route>
              )}
              {item.shrType === "VIDEO" && (
                <Route exact path={match.path + "/video"}>
                  <ArtPieceVideo />
                </Route>
              )}
              {item.shrType === "STREETART" && (
                <Route exact path={match.path + "/street-art"}>
                  <ArtPieceStreetArt />
                </Route>
              )}
              {item.shrType === "AUDIO" && (
                <Route exact path={match.path + "/audio"}>
                  <ArtPieceAudio />
                </Route>
              )}

              <button type="submit" className={"btn-blue d-none"}>
                Save Changes
              </button>
            </form>
          </div>
        </>
      )}
    </FormProvider>
  )
}

const InspectorLoader: FunctionComponent = () => {
  const [isLoading, setLoading] = useState(true)
  const [access, setAccess] = useState<iAccess>()
  const [item, setItem] = useState<iArtPiece>()
  const match = useRouteMatch<iArtPieceParams>()
  const history = useHistory()

  const loadArtPiece = () => {
    Promise.all([
      get<iResponse<iAccess>>({
        link: "/v1/access",
        query: { shrUrl: match.params.url, collection: "ArtPieces" },
      }),
      get<iResponse<iArtPiece>>({ link: "/v1/art-pieces/" + match.params.url }),
    ])
      .then((result) => {
        const [accessResult, dataResult] = result
        unstable_batchedUpdates(() => {
          setItem(dataResult.data)
          setAccess(accessResult.data)
          setLoading(false)
        })
      })
      .catch((err) => {
        if (err?.status === 403) {
          history.replace("/no-permission")
        }
      })
  }

  useMemo(() => {
    loadArtPiece()
  }, [])

  return isLoading ? (
    <Loader />
  ) : item && access ? (
    <Inspector item={item} access={access} reloadArtPiece={loadArtPiece} />
  ) : null
}
export default InspectorLoader
